import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  Button,
  CircularProgress,
  Alert,
  Box,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  VideoLibrary as VideoLibraryIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
  VideoCall as VideoCallIcon,
  Person as PersonIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { API } from '../config/apiConfig';
import DashboardPageHeader from '../components/DashboardPageHeader';

function ChannelsList() {
  const navigate = useNavigate();
  const location = useLocation();
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [channelName, setChannelName] = useState('');
  const [channelDescription, setChannelDescription] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    console.log('🔄 Component mounted, fetching channels...');
    fetchChannels();
  }, []);

  // Refresh channels when location changes (e.g., returning from edit page)
  useEffect(() => {
    if (location.pathname === '/dashboard/channels') {
      console.log('🔄 Location changed to channels page, refreshing...');
      fetchChannels();
    }
  }, [location.pathname]);

  // Also refresh when component becomes visible (handles browser back button)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && location.pathname === '/dashboard/channels') {
        console.log('🔄 Page became visible, refreshing channels...');
        fetchChannels();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [location.pathname]);

  const fetchChannels = async () => {
    try {
      setLoading(true);
      setError(''); // Clear any previous errors
      const token = localStorage.getItem('token');
      
      console.log('🔄 Fetching channels...', { token: token ? 'present' : 'missing' });
      
      const response = await axios.get(API.myChannels, {
        headers: { Authorization: `Bearer ${token}` },
      });

      console.log('📡 Channels API response:', response.data);

      if (response.data.success) {
        const channels = response.data.channels || [];
        console.log('✅ Channels loaded:', channels.length, 'channels');
        setChannels(channels);
      } else {
        console.error('❌ API returned success: false', response.data);
        setError('Failed to load channels: ' + (response.data.error || 'Unknown error'));
      }
    } catch (err) {
      console.error('❌ Error fetching channels:', err);
      setError('Failed to load channels: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleCreateChannel = async () => {
    if (!channelName.trim()) {
      alert('Channel name is required');
      return;
    }

    try {
      setCreating(true);
      const token = localStorage.getItem('token');
      const response = await axios.post(
        API.createChannel,
        {
          channelName: channelName.trim(),
          channelDescription: channelDescription.trim() || null,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      if (response.data.success) {
        setCreateDialogOpen(false);
        setChannelName('');
        setChannelDescription('');
        fetchChannels(); // Refresh list
      }
    } catch (err) {
      console.error('Error creating channel:', err);
      alert(err.response?.data?.error || 'Failed to create channel');
    } finally {
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <Container sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 2, mb: 4 }}>
      <DashboardPageHeader
        icon={VideoLibraryIcon}
        title="My channels"
        subtitle="Organize your content into channels, refresh the list, or create something new."
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: { xs: 'flex-start', sm: 'flex-end' } }}>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={fetchChannels}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            Create Channel
          </Button>
        </Box>
      </DashboardPageHeader>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {channels.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <VideoLibraryIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            You haven't created any channels yet
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
            sx={{ mt: 2 }}
          >
            Create Your First Channel
          </Button>
        </Box>
      ) : (
        <Grid container spacing={3}>
          {channels.map((channel) => (
            <Grid item xs={12} sm={6} md={4} key={channel.ChannelId}>
              <Card elevation={2} sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardActionArea
                  onClick={() => navigate(`/dashboard/channels/${channel.ChannelId}`)}
                  sx={{ flexGrow: 1 }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box
                        sx={{
                          width: 48,
                          height: 48,
                          borderRadius: '50%',
                          bgcolor: 'primary.main',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 2,
                        }}
                      >
                        <Typography variant="h5" color="white">
                          {channel.ChannelName?.[0]?.toUpperCase()}
                        </Typography>
                      </Box>
                      <Box sx={{ flexGrow: 1 }}>
                        <Typography variant="h6" component="h2" noWrap>
                          {channel.ChannelName}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                          <PersonIcon sx={{ fontSize: 14, mr: 0.5 }} />
                          {channel.OwnerFirstName} {channel.OwnerLastName}
                        </Typography>
                      </Box>
                    </Box>

                    {channel.ChannelDescription && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 2,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          display: '-webkit-box',
                          WebkitLineClamp: 2,
                          WebkitBoxOrient: 'vertical',
                        }}
                      >
                        {channel.ChannelDescription}
                      </Typography>
                    )}

                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Chip
                        icon={<VideoCallIcon />}
                        label={`${channel.ActiveVideoCount || 0} videos`}
                        size="small"
                        variant="outlined"
                      />
                      <Chip
                        icon={<VisibilityIcon />}
                        label={`${channel.TotalViews || 0} views`}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create Channel Dialog */}
      <Dialog
        open={createDialogOpen}
        onClose={() => !creating && setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Channel</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Channel Name *"
            type="text"
            fullWidth
            variant="outlined"
            value={channelName}
            onChange={(e) => setChannelName(e.target.value)}
            disabled={creating}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Description (Optional)"
            type="text"
            fullWidth
            variant="outlined"
            multiline
            rows={3}
            value={channelDescription}
            onChange={(e) => setChannelDescription(e.target.value)}
            disabled={creating}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)} disabled={creating}>
            Cancel
          </Button>
          <Button
            onClick={handleCreateChannel}
            variant="contained"
            disabled={creating || !channelName.trim()}
          >
            {creating ? <CircularProgress size={24} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default ChannelsList;

